<template>
    <v-card>
        <v-card-title>
            {{ selectedLogicType === 'skip_logic' ? 'Add Skip Logic' : 'Add Show and Hide Logic' }}
            <v-spacer/>
            <v-btn
                    icon
                    @click="$emit('close-dialog')"
            >
                <XIcon/>
            </v-btn>
        </v-card-title>
        <v-card-subtitle>
            Personalize your form's experience based on people's responses
        </v-card-subtitle>
        <v-card-text>
            <!-- Logic Type Selector -->
            <div class="logic-type-selector mb-4">
                <f-select
                        v-model="selectedLogicType"
                        :items="logicTypeOptions"
                        :item-text="item => item.text"
                        :item-value="item => item.value"
                        solo
                        flat
                        dense
                        hide-details
                        :class="[
                            'logic-type-selector__dropdown',
                            `logic-type-selector__dropdown--${selectedLogicType}`
                        ]"
                >
                    <template #selection="{item}">
                        <span>{{ item.text }}</span>
                    </template>
                    <template #item="{on, attrs, item}">
                        <v-list-item v-on="on" v-bind="attrs">
                            <v-list-item-content>
                                <v-list-item-title>{{ item.text }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action v-if="item.value === selectedLogicType" style="padding-left: 32px; min-width: 60px;">
                                <CheckIcon size="16" color="var(--v-primary-base)" />
                            </v-list-item-action>
                        </v-list-item>
                    </template>
                </f-select>
            </div>

            <!-- Skip Logic Content -->
            <SkipLogicEditor
                    v-if="selectedLogicType === 'skip_logic'"
                    :field-obj="getConditionalField"
                    :field-const="getConditionalFieldConst"
                    :field-location="allFieldsWithEnumeration[getCurrentConditionalFieldId].formattedLocation"
                    :field-id="getCurrentConditionalFieldId"
                    :initial-skip-logic="initialSkipLogic"
                    @update-skip-logic="updateSkipLogic"
            />

            <!-- Conditional Logic Content (existing) -->
            <div v-else class="conditional-logic-editor mt-4">
                <v-text-field
                        class="conditional-logic-editor__conditional-field"
                        :value="getConditionalField.title"
                        outlined
                        disabled
                        dense
                        hide-details
                >
                    <template #prepend-inner>
                        <component
                                :is="getConditionalFieldConst.ICON_COMPONENT"
                        />
                        {{ allFieldsWithEnumeration[getCurrentConditionalFieldId].formattedLocation }}
                    </template>
                </v-text-field>

                <div class="conditional-logic-editor__expression-wrapper">
                    <component
                            :is="getConditionalFieldConst.CONDITIONAL_LOGIC.EXPRESSION_COMPONENT"
                            :expression-obj="newFieldCondition.expression"
                    />
                </div>

                <div class="conditional-logic-editor__target-top-container">
                    <div class="conditional-logic-editor__target-action-wrapper">
                        Then
                        <ConditionalThenActionSelect
                                :target-obj="newFieldCondition.if_target"
                                :actions="targetActions"
                        />
                    </div>

                    <span
                            v-if="!elseTargetEnabled"
                            class="conditional-logic-editor__target-secondary-action"
                            @click="enableElseTarget"
                    >
                            + Else Condition
                    </span>
                </div>
                <ConditionalTargetSelect
                        :condition-field-id="getCurrentConditionalFieldId"
                        :target-obj="newFieldCondition.if_target"
                        :another-targets="[...(newFieldCondition.else_target ? [newFieldCondition.else_target] : [])]"
                />

                <template v-if="elseTargetEnabled">
                    <div class="conditional-logic-editor__target-top-container mt-4">
                        <div class="conditional-logic-editor__target-action-wrapper">
                            Else
                            <ConditionalThenActionSelect
                                    :target-obj="newFieldCondition.else_target"
                                    :actions="targetActions"
                            />
                        </div>
                        <span
                                class="conditional-logic-editor__target-secondary-action"
                                @click="disableElseTarget"
                        >
                            <Trash2Icon/>
                            Remove else
                        </span>
                    </div>
                    <ConditionalTargetSelect
                            :condition-field-id="getCurrentConditionalFieldId"
                            :target-obj="newFieldCondition.else_target"
                            :another-targets="[...(newFieldCondition.if_target ? [newFieldCondition.if_target] : [])]"
                    />
                </template>
            </div>
        </v-card-text>
        <v-card-actions>
            <span
                    v-if="hasActiveCondition"
                    class="conditional-logic-editor__target-secondary-action"
                    @click="removeConditionDialogModel = true"
            >
                <TrashIcon/>
                Remove condition
            </span>
            
            <v-dialog v-model="removeConditionDialogModel" width="600">
                <template #default>
                    <v-card>
                        <v-card-title>
                            Delete logic
                            <v-spacer/>
                            <v-btn
                                    icon
                                    @click="removeConditionDialogModel = false"
                            >
                                <XIcon/>
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div class="delete-logic-info">
                                <p class="mb-3">
                                    This question is being used as a destination in a
                                    {{ selectedLogicType === 'skip_logic' ? 'skip logic' : 'conditional logic' }}
                                    from question 
                                    <strong>{{ allFieldsWithEnumeration[getCurrentConditionalFieldId]?.formattedLocation }}: {{ getConditionalField?.title }}</strong>.
                                    If you remove it, that condition will be removed. The other rules of the logic will not be affected.
                                </p>
                                
                                <p class="mb-0">
                                    Do you want to continue?
                                </p>
                            </div>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer/>
                            <v-btn color="gray_100" rounded @click="removeConditionDialogModel = false">
                                Cancel
                            </v-btn>
                            <v-btn color="red_400" rounded
                                   @click="() => {removeConditionDialogModel = false; removeCondition()}">
                                Yes, delete
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </template>
            </v-dialog>
            
            <v-spacer/>
            <v-btn
                    color="gray_100" rounded
                    @click="$emit('close-dialog')"
                    class="mr-3"
            >Cancel
            </v-btn>
            
            <!-- Save button for existing conditions -->
            <div v-if="getFieldCondition" class="save-button-container">
                <v-tooltip 
                    bottom
                    :disabled="!saveButtonTooltip"
                >
                    <template #activator="{ on, attrs }">
                        <v-btn
                                color="blue_400" rounded
                                :disabled="!isFormValid || !isCurrentConditionChanged"
                                @click="applyCondition"
                                v-bind="attrs"
                                v-on="on"
                        >Save
                        </v-btn>
                    </template>
                    <span>
                        <v-icon small class="mr-1" color="orange">mdi-alert</v-icon>
                        {{ saveButtonTooltip }}
                    </span>
                </v-tooltip>
            </div>
            
            <!-- Save button for new conditions -->
            <div v-else class="save-button-container">
                <v-tooltip 
                    bottom
                    :disabled="!saveButtonTooltip"
                >
                    <template #activator="{ on, attrs }">
                        <v-btn
                                color="blue_400" rounded
                                :disabled="!isFormValid"
                                @click="applyCondition"
                                v-bind="attrs"
                                v-on="on"
                        >{{ selectedLogicType === 'skip_logic' ? 'Set Skip Logic' : 'Set Condition' }}
                        </v-btn>
                    </template>
                    <span>
                        <v-icon small class="mr-1" color="orange">mdi-alert</v-icon>
                        {{ saveButtonTooltip }}
                    </span>
                </v-tooltip>
            </div>
        </v-card-actions>
    </v-card>
</template>

<script>
    import {mapGetters} from '@/xstore'
    import {computed, onBeforeUnmount, reactive, ref, watch} from '@vue/composition-api'
    import {useVuelidate} from '@vuelidate/core'
    import {required} from '@vuelidate/validators'

    const deepEqual = require('deep-equal')
    import Vue from 'vue'
    import {FieldsEnumerationGettersUtils} from '@/store/util_modules/fields_enumeration_getters'

    export default {
        name: "ConditionalLogicEditor",
        components: {
            XIcon: () => import('vue-feather-icons/icons/XIcon'),
            Trash2Icon: () => import('vue-feather-icons/icons/Trash2Icon'),
            TrashIcon: () => import('vue-feather-icons/icons/TrashIcon'),
            CheckIcon: () => import('vue-feather-icons/icons/CheckIcon'),
            ConditionalThenActionSelect: () => import('./Elements/ConditionalThenActionSelect'),
            ConditionalTargetSelect: () => import('./Elements/ConditionalTargetSelect'),
            SkipLogicEditor: () => import('./SkipLogicEditor')
        },
        computed: {
            ...mapGetters('edit/conditional', [
                'getCurrentConditionalFieldId',
                'getConditionalField',
                'getConditionalFieldConst',
                'getFieldCondition'
            ]),
            allFieldsWithEnumeration() {
                return this.$store.getters['edit/' + FieldsEnumerationGettersUtils.allFieldsWithEnumerationGetterName]
            }
        },
        setup(props, {root}) {
            // Logic Type Options
            const logicTypeOptions = [
                {
                    text: 'Skip logic',
                    value: 'skip_logic',
                    icon: () => import('vue-feather-icons/icons/SkipForwardIcon')
                },
                {
                    text: 'Show and hide questions',
                    value: 'conditional_logic',
                    icon: () => import('vue-feather-icons/icons/GitMergeIcon')
                }
            ]

            // Selected logic type - detect based on existing condition
            const currentFieldCondition = computed(() => root.$store.getters['edit/conditional/getFieldCondition'])
            
            // Function to detect logic type from existing condition
            const detectLogicType = (condition) => {
                if (!condition) {
                    return 'skip_logic' // Default for new conditions
                }
                
                // If it has _skip_logic_data, it's definitively skip logic
                if (condition._skip_logic_data) {
                    return 'skip_logic'
                }
                // If it has else_target, it's conditional logic
                else if (condition.else_target) {
                    return 'conditional_logic'
                }
                // If it has traditional structure without _skip_logic_data, it's conditional logic
                else if (condition.expression && condition.if_target) {
                    return 'conditional_logic'
                }
                // Default to skip logic for new conditions
                else {
                    return 'skip_logic'
                }
            }
            
            const selectedLogicType = ref(detectLogicType(currentFieldCondition.value))

            const targetActions = computed(() => [
                ...(root.$store.getters['edit/isFormInteractive'] ? [{
                    icon: () => import('vue-feather-icons/icons/LogInIcon'),
                    text: 'Jump to',
                    value: 'jump_to'
                }] : []),
                {
                    icon: () => import('vue-feather-icons/icons/EyeOffIcon'),
                    text: 'Hide',
                    value: 'hide'
                },
                {
                    icon: () => import('vue-feather-icons/icons/EyeIcon'),
                    text: 'Show',
                    value: 'show'
                }
            ])

            const initialTargetObj = {
                action: root.$store.getters['edit/isFormInteractive'] ? 'jump_to' : 'hide',
                field_id: undefined
            }

            // Skip Logic state
            const initialSkipLogic = computed(() => {
                const existing = currentFieldCondition.value
                if (existing) {
                    // If it has _skip_logic_data, use that (new skip logic)
                    if (existing._skip_logic_data) {
                        return existing._skip_logic_data
                    }
                    // If it's a traditional condition with jump_to, convert it to skip logic format
                    else if (existing.if_target?.action === 'jump_to' && existing.expression) {
                        // Get all options for this field
                        const fieldConst = root.$store.getters['edit/conditional/getConditionalFieldConst']
                        const allOptions = fieldConst.CONDITIONAL_LOGIC?.PARAMS?.VALUE_FORMATTER ? 
                            fieldConst.CONDITIONAL_LOGIC.PARAMS.VALUE_FORMATTER(root.$store.getters['edit/conditional/getConditionalField']) : []

                        return {
                            type: 'skip_logic',
                            selectedOptions: allOptions.filter(option => 
                                existing.expression.statement.includes(option.index)
                            ),
                            destinations: existing.expression.statement.reduce((acc, optionIndex) => {
                                acc[optionIndex] = {
                                    type: 'question',
                                    field_id: existing.if_target.field_id
                                }
                                return acc
                            }, {})
                        }
                    }
                }
                return {
                    type: 'skip_logic',
                    selectedOptions: [],
                    destinations: {}
                }
            })

            const currentSkipLogic = ref(null)
            const skipLogicIsValid = ref(true) // Track skip logic validation state

            // Old Skip Logic Target (keep for backward compatibility)
            const skipLogicTarget = reactive({
                action: 'jump_to',
                field_id: undefined
            })

            const newFieldCondition = reactive(
                currentFieldCondition.value
                    ? (() => {
                        // Clone the existing condition but remove _skip_logic_data to prevent cross-contamination
                        const cloned = root.Utils.deepCloneObject(currentFieldCondition.value)
                        delete cloned._skip_logic_data
                        return cloned
                    })()
                    :
                    {
                        expression: {
                            operator: undefined,
                            statement: [],
                            options: undefined
                        },
                        if_target: {...initialTargetObj}
                    }
            )

            // Watch for existing condition to set the correct logic type
            // Only set the type on initial load, not when user manually changes it
            const hasUserChangedType = ref(false)
            
            watch(currentFieldCondition, (condition) => {
                // Only auto-detect logic type if user hasn't manually changed it
                if (!hasUserChangedType.value) {
                    selectedLogicType.value = detectLogicType(condition)
                }
                
                // Sync skip logic target with existing condition for backward compatibility
                if (condition && selectedLogicType.value === 'skip_logic' && condition.if_target?.field_id) {
                    skipLogicTarget.field_id = condition.if_target.field_id
                }
            }, { immediate: true })
            
            // Watch for manual user changes to the logic type dropdown
            watch(selectedLogicType, () => {
                hasUserChangedType.value = true
            })

            const elseTargetEnabled = ref(newFieldCondition.else_target)
            const enableElseTarget = () => {
                elseTargetEnabled.value = true
                Vue.set(newFieldCondition, 'else_target', reactive({...initialTargetObj}))
            }
            const disableElseTarget = () => {
                elseTargetEnabled.value = false
                Vue.delete(newFieldCondition, 'else_target')
            }

            const applyCondition = () => {
                let conditionToSave

                if (selectedLogicType.value === 'skip_logic') {
                    // Convert skip logic to traditional conditional logic structure
                    const skipLogic = currentSkipLogic.value || {
                        type: 'skip_logic',
                        selectedOptions: [],
                        destinations: {}
                    }

                    // Get all options for this field
                    const fieldConst = root.$store.getters['edit/conditional/getConditionalFieldConst']
                    const allOptions = fieldConst.CONDITIONAL_LOGIC?.PARAMS?.VALUE_FORMATTER ? 
                        fieldConst.CONDITIONAL_LOGIC.PARAMS.VALUE_FORMATTER(root.$store.getters['edit/conditional/getConditionalField']) : []

                    // Convert to traditional structure for compatibility
                    if (skipLogic.selectedOptions.length > 0 && Object.keys(skipLogic.destinations).length > 0) {
                        // Find the first option with a destination for the primary condition
                        const firstOptionWithDestination = skipLogic.selectedOptions.find(option => 
                            skipLogic.destinations[option.index]?.field_id
                        )

                        if (firstOptionWithDestination) {
                            conditionToSave = {
                                expression: {
                                    operator: 'matches_one_of',
                                    statement: [firstOptionWithDestination.index],
                                    options: undefined
                                },
                                if_target: {
                                    action: 'jump_to',
                                    field_id: skipLogic.destinations[firstOptionWithDestination.index].field_id
                                },
                                // Store the original skip logic data for future editing
                                _skip_logic_data: skipLogic
                            }
                        } else {
                            // No destinations set, create a minimal condition
                            conditionToSave = {
                                expression: {
                                    operator: 'matches_one_of',
                                    statement: skipLogic.selectedOptions.map(opt => opt.index),
                                    options: undefined
                                },
                                if_target: {
                                    action: 'jump_to',
                                    field_id: undefined
                                },
                                _skip_logic_data: skipLogic
                            }
                        }
                    } else {
                        // Empty skip logic, create minimal condition
                        conditionToSave = {
                            expression: {
                                operator: 'matches_one_of',
                                statement: [],
                                options: undefined
                            },
                            if_target: {
                                action: 'jump_to',
                                field_id: undefined
                            },
                            _skip_logic_data: skipLogic
                        }
                    }
                } else {
                    // For conditional logic, use the full newFieldCondition
                    conditionToSave = newFieldCondition
                }

                root.$store.dispatch('edit/conditional/updateConditionForField', {
                    fieldID: root.$store.getters['edit/conditional/getCurrentConditionalFieldId'],
                    condition: conditionToSave
                })
                root.$store.dispatch('edit/conditional/closeConditionalLogicEditor')
                
                // Show success toast notification
                root.snackbarEventBus.$emit('snackbar', {
                    emoji: root.constants.EMOJI.HANDS_UP,
                    color: root.constants.SNACKBAR_CONSTANTS.COLORS.SECONDARY,
                    text: 'Changes Successfully Saved'
                })
            }
            const removeConditionDialogModel = ref(false)
            const removeCondition = () => {
                root.$store.dispatch('edit/conditional/removeConditionForField', root.$store.getters['edit/conditional/getCurrentConditionalFieldId'])
                root.$store.dispatch('edit/conditional/closeConditionalLogicEditor')
                
                // Show success toast notification
                root.snackbarEventBus.$emit('snackbar', {
                    emoji: root.constants.EMOJI.HANDS_UP,
                    color: root.constants.SNACKBAR_CONSTANTS.COLORS.SECONDARY,
                    text: 'Changes Successfully Saved'
                })
            }

            const isCurrentConditionChanged = ref(false)
            const unwatch = watch(
                newFieldCondition,
                v => {
                    isCurrentConditionChanged.value = !deepEqual(v, currentFieldCondition.value)
                },
                {deep: true}
            )

            // Watch for skip logic target changes
            const unwatchSkipLogic = watch(
                skipLogicTarget,
                () => {
                    if (selectedLogicType.value === 'skip_logic') {
                        // For skip logic, check if target has changed
                        const currentTarget = currentFieldCondition.value?.if_target?.field_id
                        isCurrentConditionChanged.value = skipLogicTarget.field_id !== currentTarget
                    }
                },
                {deep: true}
            )

            // Watch for logic type changes
            const unwatchLogicType = watch(
                selectedLogicType,
                (newType, oldType) => {
                    // Only run this if user has manually changed the type (not initial load)
                    if (oldType !== undefined && newType !== oldType) {
                        const currentType = detectLogicType(currentFieldCondition.value)
                        
                        // Changing logic type IS a change that should enable the Save button
                        isCurrentConditionChanged.value = newType !== currentType
                    }
                }
            )

            // Function to handle skip logic updates from SkipLogicEditor
            const updateSkipLogic = (newSkipLogic) => {
                console.log('🔄 Skip logic validation state:', newSkipLogic.isValid)
                
                currentSkipLogic.value = newSkipLogic
                
                // Update skip logic validation state
                skipLogicIsValid.value = newSkipLogic.isValid !== false // Default to true if not specified
                
                // Update change detection for skip logic
                if (selectedLogicType.value === 'skip_logic') {
                    // If there's no existing condition, any skip logic data means there are changes
                    if (!currentFieldCondition.value) {
                        // For new skip logic, mark as changed if we have any meaningful data
                        const hasData = newSkipLogic.selectedOptions.length > 0 || 
                                       Object.keys(newSkipLogic.destinations || {}).length > 0
                        isCurrentConditionChanged.value = hasData
                    } else {
                        // For existing conditions, we need to check two things:
                        // 1. If we switched logic types, that's already a change
                        // 2. If we're in the same logic type, compare the data
                        const currentType = detectLogicType(currentFieldCondition.value)
                        
                        if (currentType !== 'skip_logic') {
                            // We switched FROM another logic type TO skip logic - this is a change
                            isCurrentConditionChanged.value = true
                        } else {
                            // We're editing existing skip logic - compare with initial state
                            const hasChanges = !deepEqual(newSkipLogic, initialSkipLogic.value)
                            isCurrentConditionChanged.value = hasChanges
                        }
                    }
                }
            }

            // Keep updateSkipTarget for backward compatibility
            const updateSkipTarget = (newTarget) => {
                skipLogicTarget.field_id = newTarget.field_id
                
                // Update change detection for skip logic
                if (selectedLogicType.value === 'skip_logic') {
                    const currentTarget = currentFieldCondition.value?.if_target?.field_id
                    isCurrentConditionChanged.value = skipLogicTarget.field_id !== currentTarget
                }
            }

            onBeforeUnmount(() => {
                unwatch()
                unwatchSkipLogic()
                unwatchLogicType()
            })


            /** VALIDATION **/
            const $v = useVuelidate(computed(() => {
                const rules = {}

                if (selectedLogicType.value === 'conditional_logic') {
                    // Conditional logic validation
                    rules.newFieldCondition = {
                        expression: {
                            operator: {required},
                            statement: {required}
                        }
                    }
                } 
                // For skip logic, we don't need strict validation
                // The button should be enabled as long as the user has interacted with the skip logic

                return rules
            }), {
                newFieldCondition
            }, {
                $stopPropagation: true
            })

            // Computed property for overall validation state
            const isFormValid = computed(() => {
                if (selectedLogicType.value === 'conditional_logic') {
                    return !$v.value.$invalid
                } else if (selectedLogicType.value === 'skip_logic') {
                    // For skip logic, check if all destinations are valid
                    return skipLogicIsValid.value
                }
                return true
            })

            // Computed property for Save button tooltip
            const saveButtonTooltip = computed(() => {
                const logicType = selectedLogicType.value
                const isValid = isFormValid.value
                const shouldShowTooltip = logicType === 'skip_logic' && !isValid
                
                console.log('🔍 Tooltip debug:', {
                    logicType,
                    isValid,
                    shouldShowTooltip,
                    skipLogicIsValid: skipLogicIsValid.value
                })
                
                if (shouldShowTooltip) {
                    return 'Please select a destination to activate this logic'
                }
                return null
            })

            // Watch for isFormValid changes (keep for debugging)
            watch(isFormValid, (newValid, oldValid) => {
                if (newValid !== oldValid) {
                    console.log('🔄 Form validation changed to:', newValid)
                }
            })

            // Watch for tooltip changes
            watch(saveButtonTooltip, (newTooltip, oldTooltip) => {
                console.log('💬 Tooltip changed:', { 
                    from: oldTooltip, 
                    to: newTooltip,
                    shouldShow: !!newTooltip
                })
            })

            // Computed property to check if there are active conditions
            const hasActiveCondition = computed(() => {
                if (selectedLogicType.value === 'skip_logic') {
                    // For skip logic, check if there are destinations configured
                    if (currentSkipLogic.value && currentSkipLogic.value.destinations) {
                        return Object.keys(currentSkipLogic.value.destinations).length > 0
                    }
                    // Also check if there's an existing condition
                    return !!currentFieldCondition.value
                } else {
                    // For conditional logic, check if there's an existing condition
                    return !!currentFieldCondition.value
                }
            })

            return {
                // Logic Type Selector
                logicTypeOptions,
                selectedLogicType,
                
                // Current condition
                currentFieldCondition,
                
                // Skip Logic
                initialSkipLogic,
                updateSkipLogic,
                
                // Backward compatibility
                skipLogicTarget,
                updateSkipTarget,

                // Existing variables
                targetActions,
                newFieldCondition,
                elseTargetEnabled,
                enableElseTarget,
                disableElseTarget,

                isCurrentConditionChanged,
                applyCondition,
                removeConditionDialogModel,
                removeCondition,
                $v,
                isFormValid,
                skipLogicIsValid,
                saveButtonTooltip,
                hasActiveCondition
            }
        }
    }
</script>

<style lang="scss">
    // Logic Type Selector
    .logic-type-selector {
        &__dropdown {
            width: fit-content;
            transition: width 0.2s ease;
            
            // Skip logic - más pequeño
            &--skip_logic {
                max-width: 110px;
                min-width: 100px;
            }
            
            // Conditional logic - más grande
            &--conditional_logic {
                max-width: 160px;
                min-width: 150px;
            }
            
            .v-input__control {
                min-width: auto;
                width: fit-content;
                transition: width 0.2s ease;
                
                .v-input__slot {
                    background-color: transparent !important;
                    box-shadow: none !important;
                    padding-left: 0 !important;
                    padding-right: 0 !important;
                    min-height: auto !important;
                    
                    .v-select__slot {
                        padding: 0 !important;
                        
                        .v-select__selections {
                            flex-wrap: nowrap;
                            padding: 0;
                            margin: 0;
                            white-space: nowrap;
                            
                            .v-select__selection {
                                font-weight: 500;
                                font-size: 1rem;
                                color: var(--v-bluish_gray_500-base);
                                margin: 0;
                                padding: 0;
                                white-space: nowrap;
                                overflow: visible;
                                text-overflow: unset;
                            }
                            
                            input {
                                display: none;
                            }
                        }
                    }
                    
                    .v-input__append-inner {
                        margin-left: 4px !important;
                        margin-right: 0 !important;
                        padding: 0 !important;
                        
                        .v-input__icon {
                            width: 16px !important;
                            height: 16px !important;
                        }
                    }
                }
            }
        }
    }

    // Skip Logic Editor styles are now in SkipLogicEditor.vue

    .conditional-logic-editor {
        padding-left: 34px;

        &__conditional-field {
            margin-bottom: 20px !important;

            &:before {
                content: "If";
                position: absolute;
                align-self: center;
                right: calc(100% + 20px);

                font-size: 1.25rem;
                color: var(--v-bluish_gray_500-base);
            }

            .v-input__control {
                .v-input__slot {
                    .v-input__prepend-inner {
                        display: flex;
                        align-items: center;
                        color: var(--v-grey_700-base);

                        svg {
                            margin-right: 12px;
                        }
                        img {
                            width: 30px;
                            height: 30px;
                            margin-right: 12px;
                        }

                        &:after {
                            content: ':';
                            align-self: center;
                        }
                    }

                    .v-text-field__slot {
                        input {
                            color: var(--v-grayscale_black-base);
                            font-weight: 500;
                        }
                    }
                }
            }
        }

        &__expression-wrapper {
            margin-bottom: 24px;

            .conditional-logic-expression {
                &__expression-input {
                    & > .v-input__control > .v-input__slot {
                        padding-left: 0 !important;

                        & > .v-input__prepend-inner {
                            padding-right: 4px;
                            margin: 0 !important;
                            align-self: auto !important;
                        }
                    }

                    margin-bottom: 2px !important;

                    .conditional-logic-expression__operator-picker {
                        align-items: stretch;

                        &:not(.v-select--is-menu-active) .v-input__control .v-input__append-inner i.v-icon {
                            transform: none;
                        }

                        .v-input__control {
                            min-height: 42px !important;
                            border-radius: 8px 0 0 8px;

                            .v-input__slot {
                                background-color: var(--v-blue_100-base) !important;
                                height: 100% !important;

                                .v-select__slot {
                                    .v-select__selections {
                                        .v-select__selection {
                                            max-width: 100%;
                                            margin: 0;
                                            font-size: .875rem;
                                            font-weight: 500;
                                        }

                                        input {
                                            width: 0;
                                            min-width: 0;
                                        }
                                    }
                                }

                            }
                        }
                    }

                    .v-select__slot {
                        .v-select__selections {
                            .v-chip {
                                border-radius: 20px !important;
                                background: #f3f3f3 !important;
                            }
                            .v-chip__content {
                                .text-truncate {
                                    max-width: 230px;
                                }
                            }
                        }
                    }
                }
            }
        }

        &__target-top-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
        }

        &__target-action-wrapper {
            display: flex;
            align-items: center;
            color: var(--v-bluish_gray_500-base);

            .conditional-logic-editor__target-action-picker {
                .v-input__control {
                    flex-grow: 0;
                    width: auto;

                    .v-input__slot {
                        .v-select__slot .v-select__selections {
                            svg {
                                color: var(--v-dark_gray-base);
                                margin-right: 6px;
                            }

                            input {
                                width: 0;
                                flex-grow: 0;
                            }
                        }
                    }
                }
            }
        }

        &__target-field {
            .v-input__control {
                .v-input__slot {
                    .v-select__slot .v-select__selections {
                        flex-wrap: nowrap;

                        & > span {
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }

                        input {
                            width: 0;
                        }
                    }
                }
            }
        }

        &__target-items {
            .v-list-item {
                max-width: 600px;

                &.v-list-item--disabled {
                    pointer-events: auto;

                    .v-list-item__icon, .v-list-item__content {
                        opacity: 0.3;
                    }
                }

                .v-list-item__content {
                    & > span {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }

                .v-list-item__action {
                    font-size: .75rem;
                    color: var(--v-gray_400-base);
                }
            }
        }

        &__target-secondary-action {
            font-size: .875rem;
            color: var(--v-gray_500-base);
            font-weight: 500;
            cursor: pointer;

            display: flex;
            align-items: center;

            svg {
                width: 20px;
                height: 20px;
                margin-right: 5px;
                stroke-width: 1px;
            }
        }
    }

    // Logic Type Selector dropdown menu items
    .v-list-item {
        &__action {
            margin: 0 !important;
            min-width: auto !important;
        }
    }

    // Delete Logic Dialog
    .delete-logic-info {
        p {
            line-height: 1.5;
            color: #111828;
            font-size: 14px;
            
            &:last-child {
                margin-bottom: 0 !important;
            }
        }
        
        strong {
            color: var(--v-gray_900-base);
            font-weight: 600;
        }
    }

    // Save button tooltip
    .save-button-container {
        display: inline-block;
    }

    .save-tooltip-content {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px 12px;
        border-radius: 6px;
        background: #1f2937;
        color: white;
        font-size: 0.875rem;
        font-weight: 500;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        white-space: nowrap;
        
        .v-icon {
            color: #f59e0b !important; // Orange warning color
            margin-right: 8px !important;
        }
    }
</style>